import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Field, reduxForm} from 'redux-form';
import S from 'StyledSessionExtension.js';
import { requestToSendSmsCodeToExtendSession, verifySmsCodeToExtendSession, logOutUser } from 'authActions.js';
import { translation } from 'helpers/utilsHelper.js';
import _pick from 'lodash/pick';
import TimerIcon from 'timer_icon.svg';

// Validation
import validation from 'sb-validate-js/validate.js';
import validators from 'validators.enum.js';

const validate = values => {
    const errors = validation(values, validators.SMS_CODE_TO_EXTEND_SESSION);
    return errors;
};

const SEND_SMS_CODE = 'SEND_SMS_CODE';
const VERIFY_SMS_CODE = 'VERIFY_SMS_CODE';
const SESSION_EXTENDED_CONFIRMATION = 'SESSION_EXTENDED_CONFIRMATION';
const SESSION_CLOSED_CONFIRMATION = 'SESSION_CLOSED_CONFIRMATION';

const maxSmsRequests = 3;

const SessionExtension = ({toggleSessionExtensionModal, showSessionClosedInfo, requestToSendSmsCodeToExtendSession, verifySmsCodeToExtendSession, logOutUser, user,
     handleSubmit, pristine, submitting, smsRequestSessionExtensionCounter, errorSendSms}) => {

    const [step, setStep] = useState(showSessionClosedInfo ? SESSION_CLOSED_CONFIRMATION : SEND_SMS_CODE);
    const [errorMessage, setErrorMessage] = useState(null);

    const endSesion = () => {
        setStep(SESSION_CLOSED_CONFIRMATION);
    };

    const logout = () => {
        logOutUser(true);

        setTimeout(()=>{window.location.reload()}, 1000);
    }

    const onFormSubmit = async(values) => {
        try {
            if (user?.prolongSession?.smsCodeAttemptsLeft == 0){
                endSesion();
                return;
            }

            let reloginData = _pick(values,['verificationCode']);
            reloginData.mobile = user.mobileNumber;
            const response = await verifySmsCodeToExtendSession(reloginData);
            if (response == true){
                setStep(SESSION_EXTENDED_CONFIRMATION);
            } else if (response == 'verificationCode_error') {
                setErrorMessage('prolongSession_wrongSmsCode');
            } else if (response == 'verificationCode_wrongSmsCode_exceeded') {
                endSesion();
            }
        } catch(ex){
            console.log(ex);
        }
    }

    return (
        <S.SendSmsCodeForm method="post" onSubmit={handleSubmit(onFormSubmit)}>
            <S.MainContainer>
                <S.SessionExtensionTopRow>
                    <S.TopRowLeftSide>
                        <S.SessionDeactive> {translation('prolongSession_sessionDeactive')} &nbsp;-&nbsp; </S.SessionDeactive>
                        <S.SessionExtensionUserDetails> {translation('prolongSession_mobileUser')} </S.SessionExtensionUserDetails>
                        <S.SessionExtensionUserMobile> &nbsp; {user?.mobileNumber} </S.SessionExtensionUserMobile>
                    </S.TopRowLeftSide>
                    <S.TopRowRightSide>
                        <S.BalanceLabel> {translation('sessionExtension_balance')} </S.BalanceLabel>
                        <S.BalanceBackground> {user?.balance?.toFixed(2)} </S.BalanceBackground>
                    </S.TopRowRightSide>
                </S.SessionExtensionTopRow>
            
                {step == SEND_SMS_CODE && <SendSmsCodeStep endSesion={endSesion} setStep={setStep} requestToSendSmsCodeToExtendSession={requestToSendSmsCodeToExtendSession} smsRequestSessionExtensionCounter={smsRequestSessionExtensionCounter} user={user}></SendSmsCodeStep>}
                {step == VERIFY_SMS_CODE && <VerifySmsCodeStep endSesion={endSesion} pristine={pristine} submitting={submitting} requestToSendSmsCodeToExtendSession={requestToSendSmsCodeToExtendSession} errorMessage={errorMessage} smsRequestSessionExtensionCounter={smsRequestSessionExtensionCounter} setStep={setStep} errorSendSms={errorSendSms} user={user}></VerifySmsCodeStep>}           
                {step == SESSION_EXTENDED_CONFIRMATION && <SessionExtendedConfirmationStep closeModal={toggleSessionExtensionModal}></SessionExtendedConfirmationStep>}
                {step == SESSION_CLOSED_CONFIRMATION && <SessionClosedConfirmationStep closeModal={logout}></SessionClosedConfirmationStep>}
            </S.MainContainer>
        </S.SendSmsCodeForm>
    );
}


const SendSmsCodeStep = ({endSesion, setStep, requestToSendSmsCodeToExtendSession, smsRequestSessionExtensionCounter, user}) => { 

    const continueSession = () => {
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
            return;
        }
        if (smsRequestSessionExtensionCounter < maxSmsRequests){
            requestToSendSmsCodeToExtendSession();
            setStep(VERIFY_SMS_CODE);
        } else {
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
    };

    return (
        <S.SessionExtensionWrapper>
            <S.TimerIcon dangerouslySetInnerHTML={{ __html: TimerIcon }}></S.TimerIcon>
            <S.SessionExtensionHighlightedMessage> {translation('prolongSession_timePassed')} </S.SessionExtensionHighlightedMessage>
            <S.SessionExtensionHighlightedMessage> {translation('prolongSession_continuePlay')} </S.SessionExtensionHighlightedMessage>
            <S.SessionExtensionMainMessage> {translation('prolongSession_mainMessage')} </S.SessionExtensionMainMessage>

            <S.ButtonsTopBorder></S.ButtonsTopBorder>
            <S.ButtonsContainer>
                <S.ButtonAction className="continue" onClick={() => continueSession()}> {translation('prolongSession_continue')} </S.ButtonAction>
                <S.ButtonAction className="leave" onClick={() => endSesion()}> {translation('prolongSession_leave')} </S.ButtonAction>
            </S.ButtonsContainer>
        </S.SessionExtensionWrapper>
    )
};


const renderField = ({
    input,
    type,
    meta: {touched, error, warning}
}) => {
    return (
        <S.InputWrapper>
            <S.SmsCodeInput {...input} type={type}/>
            {touched && ((error && <S.ErrorMessage>{error[0]}</S.ErrorMessage>) || (warning && <S.ErrorMessage>{warning}</S.ErrorMessage>))}
        </S.InputWrapper>     
    )
};


const VerifySmsCodeStep = ({endSesion, requestToSendSmsCodeToExtendSession, errorMessage=null, pristine, submitting, smsRequestSessionExtensionCounter, setStep, errorSendSms, user}) => {

    const coolDownToResend = 10;
    const [resendSmsCodeButtonDisabled, setResendSmsCodeButtonDisabled] = useState(true);
    const [counter, setCounter] = useState(-1);

    useEffect(() => {
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
        if (errorSendSms){
            setCounter(coolDownToResend);
        }
    }, [errorSendSms]);

    useEffect(() => {
        if (counter < 0){
            return;
        }
        const interval = setInterval(() => {
            if (counter >= 0){
                setCounter(counter-1);
            } 
            if (counter == 0){
                setResendSmsCodeButtonDisabled(false);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [counter]);

    const resendSmsCode = () => {
        if (user?.prolongSession?.sendSMSAttemptsLeft == 0){
            setStep(SESSION_CLOSED_CONFIRMATION);
            return;
        }

        if (smsRequestSessionExtensionCounter < maxSmsRequests){
            requestToSendSmsCodeToExtendSession();
            setResendSmsCodeButtonDisabled(true);
        } else {
            setStep(SESSION_CLOSED_CONFIRMATION);
        }
    }

    return (
        <S.SessionExtensionWrapper className="padding-top">
             <S.SessionExtensionHighlightedMessage> {translation('prolongSession_enterSmsCode')} </S.SessionExtensionHighlightedMessage>
             <S.SessionExtensionPutSmsCodeMessage> {translation('prolongSession_shouldReceiveSmsCode')} </S.SessionExtensionPutSmsCodeMessage>
             <S.InputContainer>
                <Field
                    name="verificationCode"
                    type="text"
                    component={renderField}
                />
                <S.ErrorMessage> {translation(errorMessage)} </S.ErrorMessage>
                <S.SendAgainContainer>
                    <S.Counter className={counter == -1 ? `visibility-hidden`:``}>{counter}</S.Counter>
                    <S.ResendSmsCode disabled={resendSmsCodeButtonDisabled} onClick={() => resendSmsCode()}> {translation('prolongSession_sendAgain')} </S.ResendSmsCode>
                </S.SendAgainContainer>
             </S.InputContainer>

            <S.ButtonsTopBorder></S.ButtonsTopBorder>
            <S.ButtonsContainer>
                <S.ButtonAction className="leave" onClick={() => endSesion()}> {translation('prolongSession_leave')} </S.ButtonAction>
                <S.ButtonAction type="submit" disabled={pristine || submitting} className="reLogin"> {translation('prolongSession_reLogin')} </S.ButtonAction>
            </S.ButtonsContainer>
        </S.SessionExtensionWrapper>
    );
};


const SessionExtendedConfirmationStep = ({closeModal}) => {

    return (<S.SessionExtensionWrapper className="padding-top">
        <S.SessionExtensionHighlightedMessage> {translation('prolongSession_sessionExtendedMessage1')}  </S.SessionExtensionHighlightedMessage>
        <S.SessionExtensionMainMessage> {translation('prolongSession_sessionExtendedMessage2')} </S.SessionExtensionMainMessage>
        <S.ButtonsTopBorder></S.ButtonsTopBorder>
        <S.ButtonsContainer>
            <S.ButtonAction className="confirm" onClick={() => closeModal()}> {translation('prolongSession_close')} </S.ButtonAction>
        </S.ButtonsContainer>
    </S.SessionExtensionWrapper>)
}

const SessionClosedConfirmationStep = ({closeModal}) => {

    return (<S.SessionExtensionWrapper className="padding-top">
        <S.SessionExtensionHighlightedMessage> {translation('prolongSession_sessionClosedMessage1')}  </S.SessionExtensionHighlightedMessage>
        <S.SessionExtensionMainMessage> {translation('prolongSession_sessionClosedMessage2')} </S.SessionExtensionMainMessage>
        <S.ButtonsTopBorder></S.ButtonsTopBorder>
        <S.ButtonsContainer>
            <S.ButtonAction className="confirm" onClick={() => closeModal()}> {translation('prolongSession_close')} </S.ButtonAction>
        </S.ButtonsContainer>
    </S.SessionExtensionWrapper>)
}

const mapStateToProps = (state) => {
    const {Auth: {user, error, smsRequestSessionExtensionCounter, isOpenSessionExtensionModalObject}} = state;
    return {
        user,
        errorSendSms: error,
        smsRequestSessionExtensionCounter,
        showSessionClosedInfo: isOpenSessionExtensionModalObject?.sessionClosed
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        requestToSendSmsCodeToExtendSession: bindActionCreators(requestToSendSmsCodeToExtendSession, dispatch),
        verifySmsCodeToExtendSession: bindActionCreators(verifySmsCodeToExtendSession, dispatch),
        logOutUser: bindActionCreators(logOutUser, dispatch),
        dispatch
    }
};

let SessionExtensionReduxForm = reduxForm({
    form: 'sendSmsCodeToExtendSessionForm',
    validate,
    enableReinitialize: true
})(SessionExtension)


SessionExtensionReduxForm = connect(mapStateToProps, mapDispatchToProps)(SessionExtensionReduxForm);

export default SessionExtensionReduxForm 


